import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Wine, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import { BACKEND_URL } from '../lib/api';

/**
 * "Hosted by" section on the event detail page.
 * Loads the organiser's public profile snippet via /api/organisers/by-user/:id
 * and renders a clickable card linking to /organiser/:slug. Falls back to
 * just the organiser name (no card) if the profile is private/missing.
 */
const HostedBySection = ({ event }) => {
  const [organiser, setOrganiser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!event?.organiser_id) {
      setLoading(false);
      return;
    }
    let active = true;
    axios
      .get(`${BACKEND_URL}/api/organisers/by-user/${event.organiser_id}`)
      .then((r) => { if (active) setOrganiser(r.data?.organiser); })
      .catch(() => { /* private/missing — fall back below */ })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [event?.organiser_id]);

  if (loading) return null;

  // No public profile available — render minimal text fallback so guests
  // still see who's hosting (organiser_name is always stored on the event).
  if (!organiser) {
    if (!event?.organiser_name) return null;
    return (
      <div className="mb-8" data-testid="hosted-by-fallback">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A27A] mb-1.5">Hosted by</p>
        <p className="text-[#112318] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
          {event.organiser_name}
        </p>
      </div>
    );
  }

  const profileUrl = `/organiser/${organiser.slug}`;

  return (
    <div className="mb-8" data-testid="hosted-by-section">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A27A] mb-3">Hosted by</p>
      <div className="bg-white border border-[#E5E9E6] rounded-2xl p-5 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div
            className="w-16 h-16 rounded-xl bg-[#F4F6F3] border border-[#E5E9E6] flex items-center justify-center overflow-hidden shrink-0"
            data-testid="hosted-by-logo"
          >
            {organiser.logo_url ? (
              <img src={organiser.logo_url} alt={`${organiser.organisation_name} logo`} className="w-full h-full object-cover" />
            ) : (
              <Wine className="w-7 h-7 text-[#1F3D2B]/50" strokeWidth={1.5} />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <Link
              to={profileUrl}
              className="text-xl font-semibold text-[#112318] hover:text-[#1F3D2B] transition-colors inline-flex items-center gap-1.5"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="hosted-by-name-link"
            >
              {organiser.organisation_name}
            </Link>
            {organiser.region && (
              <p className="flex items-center gap-1 text-xs text-[#4A5D50] mt-0.5">
                <MapPin className="w-3 h-3" /> {organiser.region}
              </p>
            )}
            {organiser.bio && (
              <p className="text-sm text-[#4A5D50] mt-2 line-clamp-2 leading-relaxed">
                {organiser.bio}
              </p>
            )}
            <div className="flex flex-wrap gap-2 mt-4">
              <Link to={profileUrl} data-testid="hosted-by-profile-link">
                <Button
                  variant="outline"
                  className="rounded-full text-xs border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#1F3D2B] hover:text-white h-9 px-4"
                >
                  <Building2 className="w-3.5 h-3.5 mr-1.5" />
                  View organiser profile
                </Button>
              </Link>
              <Link to={profileUrl} data-testid="hosted-by-more-events-link">
                <Button
                  variant="ghost"
                  className="rounded-full text-xs text-[#1F3D2B] hover:bg-[#F4F6F3] h-9 px-4"
                >
                  More events from this organiser
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostedBySection;
